import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { X, RefreshCw } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

/**
 * VersionDetector component handles all version checking and update notifications.
 *
 * Features:
 * - Checks for version updates every 5 minutes
 * - Initial check after 10 seconds
 * - Checks for updates when route changes
 * - Shows a compact toast at bottom-left when an update is available
 * - Skips version checking in development mode
 */
export const VersionDetector = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);

  // Skip version checking in development mode
  const isDevelopment = import.meta.env.DEV;

  const checkForUpdates = useCallback(async () => {
    // Skip version checking in development
    if (isDevelopment) {
      return;
    }

    try {
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: "no-cache",
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });

      if (response.ok) {
        const data = await response.json();
        const serverVersion = data.version;

        const localVersion = import.meta.env.VITE_APP_VERSION;

        // Compare versions
        if (localVersion && serverVersion && localVersion !== serverVersion) {
          setShowUpdateBanner(true);
        }
      }
    } catch (error) {
      console.error("Failed to check for updates:", error);
    }
  }, [isDevelopment]);

  useEffect(() => {
    // Skip version checking in development
    if (isDevelopment) {
      return;
    }

    // Check for updates every 5 minutes
    const checkInterval = setInterval(checkForUpdates, 5 * 60 * 1000);

    // Initial check after 10 seconds
    const initialCheck = setTimeout(checkForUpdates, 10000);

    return () => {
      clearInterval(checkInterval);
      clearTimeout(initialCheck);
    };
  }, [checkForUpdates, isDevelopment]);

  // Check for updates when route changes
  useEffect(() => {
    // Skip version checking in development
    if (isDevelopment) {
      return;
    }
    checkForUpdates();
  }, [location.pathname, checkForUpdates, isDevelopment]);

  function handleRefresh() {
    if ("caches" in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }
    window.location.reload();
  }

  const handleDismiss = () => {
    setShowUpdateBanner(false);
  };

  return (
    <AnimatePresence>
      {showUpdateBanner && (
        <motion.div
          className="fixed bottom-4 left-4 z-50 w-full max-w-xs md:left-[calc(var(--global-nav-width,224px)+1rem)]"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <div className="flex items-center gap-3 rounded-xl border border-border bg-background/95 p-3 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80">
            {/* Icon */}
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <RefreshCw className="h-4 w-4" />
            </span>

            {/* Text */}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium leading-tight text-foreground">
                New version available
              </p>
              <p className="truncate text-xs text-muted-foreground">
                Reload to get the latest.
              </p>
            </div>

            {/* Reload */}
            <Button
              size="sm"
              onClick={handleRefresh}
              className="h-8 shrink-0 px-3"
            >
              Reload
            </Button>

            {/* Dismiss */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDismiss}
              aria-label={t("pages.notifications.dismiss", "Dismiss notification")}
              className="h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

